import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/** Marks the events this component emits, so it ignores its own. */
const COMPONENT_SOURCE = 'complementary-upsell-component';

/**
 * Complementary Upsell Component
 *
 * Handles lazy loading of recommendations and quantity-driven auto-add to cart.
 * Each quantity change syncs directly with the cart (no submit button needed).
 *
 * Pattern: Shopify recommended lazy-loading
 * - Initial render: empty container
 * - JS fetches section via Section Rendering API
 * - If products exist: HTML has header + cards (initialized here)
 * - If no products: HTML is empty (section stays invisible)
 *
 * @see sections/complementary-upsell-mab.liquid
 */
class ComplementaryUpsellComponent extends HTMLElement {
  constructor() {
    super();
    /**
     * One entry per card. `desiredQty` is the shopper's latest intent,
     * `confirmedQty` what the cart is known to hold, and `syncing` whether a
     * drain loop is already converging one towards the other.
     * @type {Map<number, {lineItemKey: string|null, confirmedQty: number, desiredQty: number, syncing: boolean}>}
     */
    this.cards = new Map();
    this.loaded = false;
    this.debounceDelay = 200; // Premium responsiveness
  }

  connectedCallback() {
    // Set up IntersectionObserver for lazy loading
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !this.loaded) {
          this.observer.disconnect();
          this.loadRecommendations();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);

    // Refresh the recommendations when the cart changes elsewhere — products
    // become available again once they leave the cart. Guard on the emitting
    // component, the way product-form.js does: our own add would otherwise
    // re-render these cards and reset the counter the shopper just set.
    this.cartUpdateHandler = (event) => {
      event.promise
        ?.then(({ detail }) => {
          if (detail?.source === COMPONENT_SOURCE || detail?.sourceId === this.id) return;
          clearTimeout(this._cartRefreshTimeout);
          this._cartRefreshTimeout = setTimeout(() => {
            this.reconcileFromCart();
          }, 300);
        })
        .catch(() => {});
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.cartUpdateHandler);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.cartUpdateHandler);
    clearTimeout(this._cartRefreshTimeout);
  }

  /**
   * Fetch recommendations via Section Rendering API
   * The returned HTML includes header + cards only if products exist
   */
  async loadRecommendations() {
    const url = this.dataset.url;
    if (!url) {
      console.error('Complementary upsell: No data-url provided');
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the matching component in the fetched HTML
      const newComponent = doc.querySelector('complementary-upsell-component');

      if (newComponent) {
        // Replace our content with the fetched content
        // If products exist: HTML has .page-width with header + cards
        // If no products: HTML is empty (section stays invisible)
        this.innerHTML = newComponent.innerHTML;
        this.loaded = true;

        // Initialize if we have product cards
        if (this.querySelector('.complementary-upsell-mab__card')) {
          requestAnimationFrame(() => this.init());
        }
      }
    } catch (error) {
      console.error('Complementary upsell: Error loading recommendations', error);
    }
  }

  /**
   * Re-read the cart and align every card with it.
   *
   * A cart change elsewhere on the page used to re-fetch this whole section and
   * replace its markup, which threw away the shopper's counters and every line
   * key the component held — so an add made right after a change to the cart
   * drawer looked like it did nothing. Reading `/cart.js` and moving the inputs
   * is enough: the recommendations themselves only change between page loads.
   */
  async reconcileFromCart() {
    const cart = await this.fetchCart();
    // No answer is not an empty cart. Adopting one would zero every card's
    // quantity on screen and drop the line keys, so the next tap would add a
    // second line instead of updating the first.
    if (!cart) return;

    for (const [variantId, state] of this.cards) {
      // Never overwrite a card whose own write is still in flight.
      if (state.syncing) continue;

      const line = cart.items.find((item) => item.variant_id === variantId);
      state.lineItemKey = line ? line.key : null;
      state.confirmedQty = line ? line.quantity : 0;
      state.desiredQty = state.confirmedQty;

      const card = this.querySelector(
        `.complementary-upsell-mab__card[data-variant-id="${variantId}"]`
      );
      if (card) this.revertQuantity(card, state.confirmedQty);
    }
  }

  /**
   * Re-read one card's line from the cart and adopt it as the confirmed truth.
   *
   * `reconcileFromCart` aligns every card and skips any card mid-write; this is
   * the single-card counterpart used *by* a write that has just finished or
   * failed, so it deliberately does not consult `syncing`.
   *
   * @param {number} variantId
   * @param {{lineItemKey: string|null, confirmedQty: number}} state
   * @returns {Promise<number>} the quantity the cart actually holds
   */
  async settleFromCart(variantId, state) {
    const cart = await this.fetchCart();
    // This runs on the recovery path, so a failure here must not invent an
    // answer: nulling the line key would make the next tap add a second line.
    if (!cart) return state.confirmedQty;

    const line = cart.items.find((item) => item.variant_id === variantId);
    state.lineItemKey = line ? line.key : null;
    state.confirmedQty = line ? line.quantity : 0;
    return state.confirmedQty;
  }

  /**
   * Read the cart, or nothing at all.
   *
   * A rate-limited or errored request still answers with valid JSON, just
   * without `items` — so a parsed body is not proof of a cart.
   *
   * @returns {Promise<{items: Array<any>}|null>}
   */
  async fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return null;

      const cart = await response.json();
      return Array.isArray(cart?.items) ? cart : null;
    } catch (error) {
      return null;
    }
  }

  init() {
    // Initialize state for each card
    this.querySelectorAll('.complementary-upsell-mab__card').forEach((card) => {
      const variantId = parseInt(card.dataset.variantId, 10);
      if (variantId) {
        const qty = parseInt(card.querySelector('[data-quantity-input]')?.value, 10) || 0;
        this.cards.set(variantId, {
          lineItemKey: null,
          confirmedQty: qty,
          desiredQty: qty,
          syncing: false,
        });
      }
    });

    // Quantity button events
    this.querySelectorAll('[data-action="decrease"], [data-action="increase"]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleQuantityButton(e));
    });

    // Quantity input direct change events
    this.querySelectorAll('[data-quantity-input]').forEach((input) => {
      input.addEventListener('change', (e) => this.handleQuantityInput(e));
    });

    // Update decrease button states (all start disabled at qty=0)
    this.updateAllDecreaseButtons();
  }

  /**
   * Handle quantity +/- button clicks
   * @param {Event} e
   */
  handleQuantityButton(e) {
    const btn = e.currentTarget;
    const action = btn.dataset.action;
    const card = btn.closest('.complementary-upsell-mab__card');
    const input = card?.querySelector('[data-quantity-input]');

    if (!input || input.disabled) return;

    const max = parseInt(input.max, 10) || 99;
    const step = parseInt(input.step, 10) || 1;
    let currentQty = parseInt(input.value, 10) || 0;

    let newQty;
    if (action === 'decrease') {
      newQty = Math.max(0, currentQty - step);
    } else if (action === 'increase') {
      newQty = Math.min(max, currentQty + step);
    } else {
      return;
    }

    // Optimistic UI update
    input.value = newQty;
    this.updateDecreaseButtonState(card, newQty);
    this.updateInCartBadge(card, newQty > 0);

    // The flight plays on the press, before the request, exactly as the native
    // button does. newQty > currentQty is what makes a press at max silent:
    // nothing is being added, so nothing should fly.
    if (action === 'increase' && newQty > currentQty) {
      this.#flyToCart(btn, card);
    } else if (action === 'decrease' && newQty < currentQty) {
      this.#flyToCart(btn, card, { returning: true });
    }

    // Schedule cart sync
    this.scheduleCartSync(card, newQty);
  }

  /**
   * Plays the theme's add-to-cart flight, from the pressed control to the cart
   * icon in the header.
   *
   * This mirrors AddToCartComponent#animateFlyToCart rather than reimplementing
   * it: the same `fly-to-cart` custom element (defined globally by
   * snippets/scripts.liquid, so it is available here), the same
   * `fly-to-cart--quick` timing curves used by quick-add on a card, and the same
   * `settings.add_to_cart_animation` gate, which the section forwards as a data
   * attribute. The element removes itself once its animations finish.
   *
   * Removing one plays the same flight in reverse, so the image leaves the cart and settles back
   * on the card it came from. The endpoints are not swapped for that: the element still measures
   * from the card to the cart, and `animation-direction: reverse` is what turns the journey
   * around. Swapping them instead would keep the arrival gesture — shrinking into a circle — and
   * play it on the card, which reads as adding rather than returning.
   *
   * @param {HTMLElement} btn - The pressed control, used as the flight's origin.
   * @param {HTMLElement} card - The card whose product image flies.
   * @param {{returning?: boolean}} [options] - `returning` plays the flight backwards.
   */
  #flyToCart(btn, card, { returning = false } = {}) {
    if (this.dataset.addToCartAnimation !== 'true') return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = card.querySelector('img');
    if (!cartIcon || !image) return;

    const source = image.currentSrc || image.src;
    if (!source) return;

    const fly = document.createElement('fly-to-cart');
    fly.classList.add('fly-to-cart--quick');
    if (returning) fly.classList.add('fly-to-cart--return');
    fly.style.setProperty('background-image', `url(${source})`);
    fly.style.setProperty('--start-opacity', '0');
    fly.source = btn;
    fly.destination = cartIcon;

    document.body.appendChild(fly);
  }

  /**
   * Handle direct input change
   * @param {Event} e
   */
  handleQuantityInput(e) {
    const input = e.target;
    const card = input.closest('.complementary-upsell-mab__card');

    if (!card) return;

    const max = parseInt(input.max, 10) || 99;
    let newQty = parseInt(input.value, 10) || 0;

    // Clamp to valid range
    newQty = Math.max(0, Math.min(max, newQty));
    input.value = newQty;

    this.updateDecreaseButtonState(card, newQty);
    this.updateInCartBadge(card, newQty > 0);
    this.scheduleCartSync(card, newQty);
  }

  /**
   * Record the shopper's intent and let the card's drain loop converge on it.
   *
   * The previous version aborted the in-flight request on every click. A cart
   * write that has already reached Shopify does not un-happen when its fetch is
   * aborted, so `confirmedQty` and `lineItemKey` were left describing a cart
   * that had moved on — which is why a fast second click could add a line twice,
   * or appear to do nothing at all. Nothing is aborted now: writes for one card
   * are serialised, and the loop always targets the latest quantity.
   *
   * @param {HTMLElement} card
   * @param {number} quantity
   */
  scheduleCartSync(card, quantity) {
    const variantId = parseInt(card.dataset.variantId, 10);
    const state = this.cards.get(variantId);

    if (!state) return;

    state.desiredQty = quantity;

    clearTimeout(card._syncTimeout);
    card._syncTimeout = setTimeout(() => this.drainCard(card), this.debounceDelay);
  }

  /**
   * Walk one card's cart state towards its desired quantity, one request at a
   * time. Re-reading `desiredQty` each turn means a burst of clicks costs one
   * extra request at most, and a click arriving mid-request is picked up by the
   * loop that is already running rather than racing it.
   *
   * @param {HTMLElement} card
   */
  async drainCard(card) {
    const variantId = parseInt(card.dataset.variantId, 10);
    const state = this.cards.get(variantId);

    if (!state || state.syncing) return;

    state.syncing = true;
    card.classList.add('complementary-upsell-mab__card--loading');

    try {
      while (state.desiredQty !== state.confirmedQty) {
        await this.applyQuantity(card, variantId, state, state.desiredQty);
      }
    } catch (error) {
      console.error('Complementary upsell: Cart sync failed', error);
      // A rejected write does not mean the cart is unchanged. Shopify answers an
      // over-quantity add with 422 *after* adding what it could ("Only 1 item was
      // added to your cart due to availability"), so assuming failure left the
      // card showing 0 while the cart held 1 — and with no line key, the next
      // click added a second line instead of updating the first. Read the cart
      // back before repainting, the way 4.1.4's own product-form does.
      await this.settleFromCart(variantId, state).catch(() => {});
      state.desiredQty = state.confirmedQty;
      this.revertQuantity(card, state.confirmedQty);
      this.showErrorFeedback(card);
    } finally {
      state.syncing = false;
      card.classList.remove('complementary-upsell-mab__card--loading');
    }
  }

  /**
   * Move one card's line to `target`, then announce it.
   * @param {HTMLElement} card
   * @param {number} variantId
   * @param {{lineItemKey: string|null, confirmedQty: number, desiredQty: number, syncing: boolean}} state
   * @param {number} target
   */
  async applyQuantity(card, variantId, state, target) {
    const title = card.dataset.productTitle || 'Item';

    if (target === 0 && !state.lineItemKey) {
      state.confirmedQty = 0;
      return;
    }

    /** @type {'add'|'update'|'remove'} */
    let action;

    if (target === 0) {
      await this.changeCartLine(state.lineItemKey, 0);
      state.lineItemKey = null;
      state.confirmedQty = 0;
      action = 'remove';
      this.announceToScreenReader(`${title} removed from cart`);
    } else if (!state.lineItemKey) {
      const line = await this.addToCart(variantId, target);
      state.lineItemKey = line.key ?? null;
      // The line's own quantity, not the quantity asked for: an add against an
      // existing line returns their sum, and Shopify may cap it.
      state.confirmedQty = typeof line.quantity === 'number' ? line.quantity : target;
      action = 'add';
      this.showAddedFeedback(card);
      this.announceToScreenReader(`${title} added to cart`);
    } else {
      const cart = await this.changeCartLine(state.lineItemKey, target);
      const line = (cart?.items || []).find((item) => item.key === state.lineItemKey);
      if (!line) state.lineItemKey = null;
      state.confirmedQty = line ? line.quantity : 0;
      action = 'update';
      this.announceToScreenReader(`${title} quantity updated to ${state.confirmedQty}`);
    }

    // Shopify caps a line at the stock it can honour. Accept the cap as the new
    // intent, otherwise the drain loop would keep re-requesting a quantity the
    // cart will never reach, and the counter would advertise stock that is gone.
    if (state.confirmedQty < target) {
      state.desiredQty = state.confirmedQty;
      this.revertQuantity(card, state.confirmedQty);
    }

    this.dispatchCartUpdate(action, variantId, state.confirmedQty);
  }

  /**
   * Add item to cart
   * @param {number} variantId
   * @param {number} quantity
   * @returns {Promise<{key: string}>}
   */
  async addToCart(variantId, quantity) {
    // FormData through fetchConfig('javascript') is the shape 4.1.4's own
    // add-to-cart uses, so this component shares its headers and can ask for
    // `sections` — the re-rendered cart markup the drawer needs.
    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', String(quantity));
    const sections = this.cartSectionIds();
    if (sections) {
      body.append('sections', sections);
      body.append('sections_url', window.location.pathname);
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('javascript', { body }),
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || `HTTP ${response.status}`);
    }

    this.renderCartSections(data.sections);

    // A single-item add returns the line itself, not an `items` array.
    return data.items ? data.items[0] : data;
  }

  /**
   * Change cart line quantity (or remove if quantity is 0)
   * @param {string} key
   * @param {number} quantity
   * @returns {Promise<Object>}
   */
  async changeCartLine(key, quantity) {
    const payload = { id: key, quantity };
    const sections = this.cartSectionIds();
    if (sections) {
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(Theme.routes.cart_change_url, {
      ...fetchConfig('json', { body: JSON.stringify(payload) }),
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || `HTTP ${response.status}`);
    }

    this.renderCartSections(data.sections);

    return data;
  }

  /**
   * Section ids of every cart-items component on the page, so the cart response
   * carries their re-rendered markup.
   * @returns {string}
   */
  cartSectionIds() {
    const ids = new Set();
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.add(item.dataset.sectionId);
    }
    return Array.from(ids).join(',');
  }

  /**
   * Paint the cart sections the cart response returned, so the drawer and the
   * cart page reflect the change without a reload.
   * @param {Record<string, string>|undefined} sections
   */
  renderCartSections(sections) {
    if (!sections) return;
    for (const [sectionId, html] of Object.entries(sections)) {
      if (typeof html !== 'string') continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
      if (!fresh) continue;

      for (const live of document.querySelectorAll(`cart-items-component[data-section-id="${sectionId}"]`)) {
        live.innerHTML = fresh.innerHTML;
      }
    }
  }

  /**
   * Tell the rest of the theme the cart changed.
   *
   * 3.4.0 listened for a bespoke `cart:update` CustomEvent on document. 4.1.4
   * routes every cart change through the Standard Events contract
   * (`shopify:cart:lines-update`) and awaits `event.promise` for the new cart —
   * so the old event reached nothing and the cart bubble, drawer and cart page
   * all stayed stale until a reload.
   *
   * @param {'add'|'update'|'remove'} action
   * @param {string} merchandiseId
   * @param {number} quantity
   */
  async dispatchCartUpdate(action, merchandiseId, quantity) {
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'product',
        lines: [{ merchandiseId: String(merchandiseId), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const cart = await this.fetchCart();
      // Resolving with a body that is not a cart would hand every listener in
      // the theme an event whose `items` is undefined. Let them take their
      // error path instead.
      if (!cart) throw new Error('Cart unavailable');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: COMPONENT_SOURCE,
          sourceId: this.id,
          itemCount: quantity,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
    }
  }

  /**
   * Revert quantity to confirmed state after error
   * @param {HTMLElement} card
   * @param {number} confirmedQty
   */
  revertQuantity(card, confirmedQty) {
    const input = card.querySelector('[data-quantity-input]');
    if (input) {
      input.value = confirmedQty;
      this.updateDecreaseButtonState(card, confirmedQty);
      this.updateInCartBadge(card, confirmedQty > 0);
    }
  }

  /**
   * Update decrease button disabled state
   * @param {HTMLElement} card
   * @param {number} quantity
   */
  updateDecreaseButtonState(card, quantity) {
    const decreaseBtn = card.querySelector('[data-action="decrease"]');
    if (decreaseBtn) {
      decreaseBtn.disabled = quantity <= 0;
    }
  }

  /**
   * Update all decrease buttons based on current quantities
   */
  updateAllDecreaseButtons() {
    this.querySelectorAll('.complementary-upsell-mab__card').forEach((card) => {
      const input = card.querySelector('[data-quantity-input]');
      const qty = parseInt(input?.value, 10) || 0;
      this.updateDecreaseButtonState(card, qty);
    });
  }

  /**
   * Update "In Cart" badge visibility
   * @param {HTMLElement} card
   * @param {boolean} inCart
   */
  updateInCartBadge(card, inCart) {
    const badge = card.querySelector('[data-in-cart-badge]');
    if (badge) {
      badge.hidden = !inCart;
    }
    card.classList.toggle('complementary-upsell-mab__card--in-cart', inCart);
  }

  /**
   * Announce message to screen readers via ARIA live region
   * @param {string} text
   */
  announceToScreenReader(text) {
    const liveRegion = this.querySelector('[data-live-region]');
    if (liveRegion) {
      liveRegion.textContent = text;
      // Clear after announcement
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1000);
    }
  }

  /**
   * Show visual feedback when item is added
   * @param {HTMLElement} card
   */
  showAddedFeedback(card) {
    card.classList.add('complementary-upsell-mab__card--added');
    setTimeout(() => {
      card.classList.remove('complementary-upsell-mab__card--added');
    }, 2000);
  }

  /**
   * Show visual feedback on error (shake animation)
   * @param {HTMLElement} card
   */
  showErrorFeedback(card) {
    card.classList.add('complementary-upsell-mab__card--error');
    this.announceToScreenReader('Error updating cart');
    setTimeout(() => {
      card.classList.remove('complementary-upsell-mab__card--error');
    }, 3000);
  }
}

// Register the custom element
if (!customElements.get('complementary-upsell-component')) {
  customElements.define('complementary-upsell-component', ComplementaryUpsellComponent);
}
